export type PostAuthor = {
  id?: string | number;
  name: string;
  icon?: string;
};

// Co-authors arrive as "author" relatives whose `thumbnail` is an absolute media
// URL, while `author.icon` is a media path. Keep everything as a path so callers
// can keep prefixing MEDIA_URL.
const toMediaPath = (url?: string) => {
  if (!url) return "";
  if (!url.startsWith("http")) return url;
  try {
    return new URL(url).pathname;
  } catch {
    return "";
  }
};

/**
 * Returns every author of a post: the main `author` followed by the
 * co-authors listed under the "author" relation. The API puts a `null`
 * placeholder where the main author would repeat, so nulls are skipped
 * and authors are de-duplicated by id.
 */
export const getPostAuthors = (post: any): PostAuthor[] => {
  const authors: PostAuthor[] = [];
  const seen = new Set<string>();

  const add = (author: PostAuthor) => {
    if (!author.name) return;
    const key = author.id ? String(author.id) : author.name;
    if (seen.has(key)) return;
    seen.add(key);
    authors.push(author);
  };

  if (post?.author?.name) {
    add({
      id: post.author.id,
      name: post.author.name,
      icon: toMediaPath(post.author.icon),
    });
  }

  const relativeAuthors =
    post?.relatives?.find((rel: any) => rel?.relationName === "author")
      ?.posts ?? [];

  relativeAuthors.forEach((rel: any) => {
    if (!rel) return;
    add({
      id: rel.id,
      name: rel.title,
      icon: toMediaPath(rel.icon || rel.image || rel.thumbnail),
    });
  });

  return authors;
};
